import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import tenant site editor component (lazy-loaded for performance)
// This is for ADMIN or SITE_EDITOR_ADMIN users editing their own linked site
const TenantSiteEditor = React.lazy(() => import("./TenantSiteEditor"));

/**
 * Site Editor Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (siteeditor/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/siteeditor/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const siteEditorModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "site-editor",
    name: "Site Editor",
    description: "Edit your website with Claude AI",
    version: "3.2.0",
    enabled: true,
    category: "development",
    order: 50,
    icon: "🌐",
    color: "#059669",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/site-editor",
            component: TenantSiteEditor,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { noContainer: true },
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "Edit My Site",
            path: "/site-editor",
            icon: "🌐",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/siteeditor/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Edit My Site",
            path: "/site-editor",
            icon: "🌐",
            description: "Edit your website with AI assistance"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "3.2.0",
            releaseDate: "2026-01-08",
            title: "Non-Developer Friendly UX",
            features: [
                "Friendlier Terminology: Replaced 'Pull Request' with 'Submit for Review', 'Merge to Main' with 'Go Live Now'",
                "Contextual Guidance: Smart banner guides users on what to do next based on their current state",
                "Clearer Workflow: Updated 'How It Works' section with plain English descriptions",
                "Improved Labels: 'End Session' now 'Save & End Session', clearer button tooltips throughout"
            ],
            notes: "UX improvements for non-technical users. All technical Git/PR terminology replaced with user-friendly alternatives."
        },
        {
            version: "3.1.0",
            releaseDate: "2026-01-07",
            title: "Direct Deploy to Production",
            features: [
                "Merge to Main: Deploy changes directly to production without waiting for PR review",
                "Deploy History: View all past deployments with file changes and timestamps",
                "Email Notifications: Receive email when changes are deployed to production",
                "Page Context: Easily add current page path to prompts in split view"
            ],
            notes: "Relay updated to v3.3 with merge-to-main support. Email notifications sent to thomasmillerprivate@gmail.com."
        },
        {
            version: "3.0.0",
            releaseDate: "2026-01-04",
            title: "Major UX Overhaul - Session Persistence & Split View",
            features: [
                "Session Persistence: Changes now accumulate on a persistent draft branch (edits/draft-{siteId}) - no more losing edits between sessions",
                "Split-View UI: Edit and preview side-by-side with embedded iframe, URL bar, and refresh button",
                "Section Identifier Tool: Bookmarklet to find and copy section IDs for precise prompts",
                "Branch Dashboard: View branch stats, modified files list, and reset branch option",
                "One Session Per Site: Session locking prevents conflicts, with takeover option",
                "Prompt Enhancement: Detects vague terms and suggests section ID format, quick templates for common edits",
                "Share & Review: Start preview sessions, create PRs, copy shareable URLs",
                "Dark mode visibility fixes throughout"
            ],
            notes: "Based on Sun & Monica feedback session (2025-12-18). Relay updated to v3.2 with session locking support."
        },
        {
            version: "2.0.0",
            releaseDate: "2025-12-12",
            title: "Tenant-Focused Site Editor",
            features: [
                "Session-based editing with automatic PR creation",
                "Start/stop editing sessions on demand",
                "Preview changes on dev server before going live",
                "AI code quality review on every save",
                "Command history tracking"
            ],
            notes: "Site Editor now focused on tenant self-service. Master admin functionality moved to Master Site Editor module."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-12-04",
            title: "Initial Release",
            features: [
                "AI-powered website editing via Claude Code",
                "Multi-site management",
                "Command history tracking",
                "Real-time edit execution",
                "Relay service integration"
            ],
            notes: "Initial implementation of Site Editor module"
        }
    ],

    // ROADMAP
    roadmap: []
};

export default siteEditorModuleConfig;
